if (!customElements.get('creditcard-form')) {
    class CreditcardForm extends HTMLElement {
        constructor() {
            super();
            this.form = null;
            this.checkoutBtn = null;
            this.customizeInterval = null;
            this.isUpdateCard = false;
            this.orderNumber = null;
            this.orderToken = null;
        }

        connectedCallback() {
            this.init();
        }

        disconnectedCallback() {
            // Clean up
            if (this.checkoutBtn) {
                this.checkoutBtn.removeEventListener('click', this.boundBeforeSubmit);
            }
            if (this.customizeInterval) {
                clearInterval(this.customizeInterval);
            }
        }

        init() {
            this.form = this.querySelector('#checkout-form');
            if (!this.form) return;

            // Initialize checkout button
            this.checkoutBtn = this.querySelector('#btn_go_to_confirm_page');
            if (this.checkoutBtn) {
                // Check if this is an update card flow
                this.isUpdateCard = this.checkoutBtn.hasAttribute('data-update-card') && 
                                    this.checkoutBtn.getAttribute('data-update-card') === 'true';
                
                this.boundBeforeSubmit = this.beforeSubmit.bind(this);
                this.checkoutBtn.addEventListener('click', this.boundBeforeSubmit);
            }

            // Customize Credix UI
            this.customizeCredixUI();

            // If the page was reloaded after an enrol failure, surface the error now
            // (on the freshly re-initialised card form).
            try {
                if (sessionStorage.getItem('credix_enrol_error') === '1') {
                    sessionStorage.removeItem('credix_enrol_error');
                    setTimeout(() => this.showEnrolError(), 300);
                }
            } catch (e) {}
        }

        // Helper function to get URL parameter
        getUrlParameter(name) {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get(name);
        }

        // Helper function to check if source=ams
        isAmsSource() {
            return this.getUrlParameter('source') === 'ams';
        }

        // Define getSaleInfo if not already defined (fallback)
        getSaleInfo(customerId) {
            if (typeof window.getSaleInfo === 'function') {
                return window.getSaleInfo(customerId);
            }

            // Fallback implementation
            return (async () => {
                try {
                    if (!customerId) {
                        return { success: false, exists: false };
                    }

                    const saleInfoApiUrl = `${window.shopify_server_url}/sale/user-sale-info?customerId=${customerId}`;

                    const response = await fetch(saleInfoApiUrl, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Origin': window.location.origin,
                            'Accept-Language': Shopify.locale || 'ja',
                            'X-Sig': window.sz_config?.shopify_server_key,
                            'X-Epoch': window.sz_config?.timestamp
                        }
                    });

                    const result = await response.json();

                    if (response.ok && result && (result.id || result.customerId || result.ssoId)) {
                        return { success: true, exists: true, data: result };
                    } else if (response.status === 404 || !result || (!result.id && !result.customerId && !result.ssoId)) {
                        return { success: true, exists: false };
                    } else {
                        console.warn('Failed to get sale info:', result);
                        return { success: false, exists: false, error: result };
                    }
                } catch (error) {
                    console.error('Error getting sale info:', error);
                    return { success: false, exists: false, error: error.message };
                }
            })();
        }

        // Build saleContract from saleInfo
        buildSaleContractFromSaleInfo(saleInfo) {
            if (!saleInfo || !saleInfo.data) {
                return null;
            }

            const data = saleInfo.data;
            
            // Get address ONLY from default_address_content input (no fallback to API)
            let address = {};
            const addressContentInput = document.querySelector('#default_address_content');
            if (addressContentInput && addressContentInput.value) {
                try {
                    address = JSON.parse(addressContentInput.value);
                } catch (err) {
                    console.warn('Error parsing address from default_address_content:', err);
                    address = {};
                }
            }

            // Helper function to safely get address field from Shopify address format
            const getAddressField = (field, fallback = '') => {
                if (!address || typeof address !== 'object') return fallback;
                // Shopify address format uses: last_name, first_name, zip, etc.
                if (field === 'last_name') {
                    return address.last_name || fallback;
                } else if (field === 'first_name') {
                    return address.first_name || fallback;
                } else if (field === 'postal_zip_code') {
                    return address.zip || fallback;
                } else if (field === 'address1') {
                    return address.address1 || fallback;
                } else if (field === 'address2') {
                    return address.address2 || fallback;
                } else {
                    return address[field] || fallback;
                }
            };

            // Map sale info to saleContract format
            const saleContract = {
                // Recipient information (ONLY from default_address_content input)
                email: window.customerEmail || '',
                last_name: getAddressField('last_name', ''),
                first_name: getAddressField('first_name', ''),
                country: getAddressField('country', ''),
                phone: getAddressField('phone', ''),
                province: getAddressField('province', ''),
                postal_zip_code: getAddressField('postal_zip_code', ''),
                city: getAddressField('city', ''),
                address1: getAddressField('address1', ''),
                address2: getAddressField('address2', ''),
                ip: window.customerIp || null,

                // Contract receiving flag
                is_receiving_contract: data.isReceivingContract !== undefined ? data.isReceivingContract : true,
                language: Shopify.locale,

                // Contract holder information
                contract_holder_full_name: data.contractHolderFullName || '',
                ...(data.contractDeliveryMethod === 'electronic' ? { electronic_holder_full_name: getCookie('electronic-holder-name') || '' } : {}),
                contract_holder_email: data.contractHolderEmail || '',
                contract_holder_phone: data.contractHolderPhone || '',
                contract_holder_address: data.contractHolderAddress || '',

                // Additional emails
                additional_emails: data.additionalEmails || '',

                // Address ID (from address input only)
                address_id: (address && address.id) ? address.id : null,

                // Sales representative information
                contract_delivery_method: data.contractDeliveryMethod,

                // Agreement time - only for electronic delivery
                ...(data.contractDeliveryMethod === 'electronic' ? { agreement_time: new Date().toISOString() } : {}),
                sale_department: data.saleDepartment || '',
                sale_phone_number: data.salePhoneNumber || '',
                sale_email: data.saleEmail || ''
            };

            // Add sale_name if exists
            if (data.saleName) {
                saleContract.sale_name = data.saleName;
            }

            return saleContract;
        }

        // Helper function to show/hide error message
        showFieldError(field, message) {
            if (!field) {
                return;
            }

            // Remove existing error message for this field
            let existingError = null;
            
            // For expiry date, check after credix_token_card_expires_year (inside payment-input-wrapper)
            if (field.id === 'credix_token_card_expires_month' || field.id === 'credix_token_card_expires_year') {
                const yearSelect = document.querySelector('#credix_token_card_expires_year');
                if (yearSelect) {
                    // Check if error is right after yearSelect (inside wrapper)
                    if (yearSelect.nextSibling && yearSelect.nextSibling.classList && yearSelect.nextSibling.classList.contains('field-error-message')) {
                        existingError = yearSelect.nextSibling;
                    }
                    // Also check inside wrapper (anywhere in wrapper)
                    if (!existingError) {
                        const wrapper = yearSelect.parentElement; // payment-input-wrapper
                        if (wrapper) {
                            existingError = wrapper.querySelector('.field-error-message');
                        }
                    }
                    // Also check after wrapper (in case error was inserted outside)
                    if (!existingError) {
                        const wrapper = yearSelect.parentElement;
                        if (wrapper && wrapper.nextSibling && wrapper.nextSibling.classList && wrapper.nextSibling.classList.contains('field-error-message')) {
                            existingError = wrapper.nextSibling;
                        }
                    }
                }
            } else {
                // For other fields, check if error is after the field itself (most specific)
                if (field.nextSibling && field.nextSibling.classList && field.nextSibling.classList.contains('field-error-message')) {
                    existingError = field.nextSibling;
                }
                // Check if error is after the direct wrapper (for card number, this is payment-input-wrapper)
                if (!existingError) {
                    const directWrapper = field.closest('.payment-input-wrapper');
                    if (directWrapper && directWrapper.nextSibling && directWrapper.nextSibling.classList && directWrapper.nextSibling.classList.contains('field-error-message')) {
                        existingError = directWrapper.nextSibling;
                    }
                }
                // For card number specifically, also check after credix_new_card_area (but only if it's card number)
                if (!existingError && field.id === 'credix_token_card_number') {
                    const credixArea = field.closest('#credix_new_card_area');
                    if (credixArea && credixArea.nextSibling && credixArea.nextSibling.classList && credixArea.nextSibling.classList.contains('field-error-message')) {
                        existingError = credixArea.nextSibling;
                    }
                }
                // Only check inside wrapper if we haven't found error yet, and only for the direct wrapper (not credix_new_card_area to avoid conflicts)
                if (!existingError) {
                    const directWrapper = field.closest('.payment-input-wrapper');
                    if (directWrapper) {
                        // Only look for error messages that are direct children of the wrapper, not nested
                        const allErrors = directWrapper.querySelectorAll('.field-error-message');
                        // Find the one that is closest to this field (right after it or in the same container)
                        for (let error of allErrors) {
                            // Check if this error is right after the field
                            if (error.previousElementSibling === field) {
                                existingError = error;
                                break;
                            }
                        }
                    }
                }
            }
            
            if (existingError) {
                existingError.remove();
            }

            if (message) {
                // Create error message element
                const errorEl = document.createElement('div');
                errorEl.className = 'field-error-message';
                errorEl.style.color = '#dc2626';
                errorEl.style.fontSize = '14px';
                errorEl.style.marginTop = '4px';
                errorEl.textContent = message;
                
                // For expiry date fields, insert after credix_token_card_expires_year inside payment-input-wrapper
                if (field.id === 'credix_token_card_expires_month' || field.id === 'credix_token_card_expires_year') {
                    // Find the year select element
                    const yearSelect = document.querySelector('#credix_token_card_expires_year');
                    if (yearSelect) {
                        // Insert after the year select, inside the payment-input-wrapper
                        const wrapper = yearSelect.parentElement; // This should be payment-input-wrapper
                        if (wrapper) {
                            // Insert right after yearSelect, inside the wrapper
                            if (yearSelect.nextSibling) {
                                wrapper.insertBefore(errorEl, yearSelect.nextSibling);
                            } else {
                                wrapper.appendChild(errorEl);
                            }
                        } else {
                            // Fallback: insert after the year select
                            if (yearSelect.nextSibling) {
                                yearSelect.parentElement.insertBefore(errorEl, yearSelect.nextSibling);
                            } else {
                                yearSelect.parentElement.appendChild(errorEl);
                            }
                        }
                    } else {
                        // Fallback: insert after the field
                        if (field.nextSibling) {
                            field.parentElement.insertBefore(errorEl, field.nextSibling);
                        } else {
                            field.parentElement.appendChild(errorEl);
                        }
                    }
                } else {
                    // For other fields, insert directly after the input
                    if (field.nextSibling) {
                        field.parentElement.insertBefore(errorEl, field.nextSibling);
                    } else {
                        field.parentElement.appendChild(errorEl);
                    }
                }
            }
        }

        beforeSubmit() {
            // Validate all required fields
            // Credix renders inputs into #credix_token_card_info_area, so search there first
            const credixArea = this.querySelector('#credix_token_card_info_area') || document.querySelector('#credix_token_card_info_area');
            const cardNumberEl = credixArea ? credixArea.querySelector('#credix_token_card_number') : (this.querySelector('#credix_token_card_number') || document.querySelector('#credix_token_card_number'));
            const cardExpMonthEl = credixArea ? credixArea.querySelector('#credix_token_card_expires_month') : (this.querySelector('#credix_token_card_expires_month') || document.querySelector('#credix_token_card_expires_month'));
            const cardExpYearEl = credixArea ? credixArea.querySelector('#credix_token_card_expires_year') : (this.querySelector('#credix_token_card_expires_year') || document.querySelector('#credix_token_card_expires_year'));
            const cardCvvEl = credixArea ? credixArea.querySelector('#credix_token_card_cvv') : (this.querySelector('#credix_token_card_cvv') || document.querySelector('#credix_token_card_cvv'));
            const cardNameEl = credixArea ? credixArea.querySelector('#credix_token_card_name') : (this.querySelector('#credix_token_card_name') || document.querySelector('#credix_token_card_name'));

            let isValid = true;

            // Validate card number
            if (!cardNumberEl) {
                isValid = false;
            } else if (!cardNumberEl.value || cardNumberEl.value.trim() === '') {
                this.showFieldError(cardNumberEl, theme.strings.credix.msg_required_cardnumber);
                isValid = false;
            } else {
                this.showFieldError(cardNumberEl, '');
            }

            // Validate name on card
            if (!cardNameEl || !cardNameEl.value || cardNameEl.value.trim() === '') {
                this.showFieldError(cardNameEl, theme.strings.credix.msg_required_cardname);
                isValid = false;
            } else {
                this.showFieldError(cardNameEl, '');
            }

            // Validate expiry date
            if (!cardExpMonthEl || !cardExpMonthEl.value || cardExpMonthEl.value === '' ||
                !cardExpYearEl || !cardExpYearEl.value || cardExpYearEl.value === '') {
                this.showFieldError(cardExpMonthEl, theme.strings.credix.msg_required_expiration);
                isValid = false;
            } else {
                // Clear error if both fields are filled
                this.showFieldError(cardExpMonthEl, '');
            }

            // Validate CVC
            if (!cardCvvEl || !cardCvvEl.value || cardCvvEl.value.trim() === '') {
                this.showFieldError(cardCvvEl, theme.strings.credix.msg_required_cvc);
                isValid = false;
            } else {
                this.showFieldError(cardCvvEl, '');
            }

            // If validation fails, stop here
            if (!isValid) {
                return;
            }

            // All fields are valid, proceed with token generation
            const self = this;
            if (typeof credixToken !== 'undefined' && credixToken.getToken) {
                credixToken.getToken(function(credix_token_response_data) {
                    if (!credix_token_response_data['result']) {
                        if (typeof showToast === 'function') {
                            showToast(credixToken.getErrorMessage(credix_token_response_data['error_code']), 'error');
                        } else {
                            alert(credixToken.getErrorMessage(credix_token_response_data['error_code']));
                        }
                    } else {
                        // In case of success
                        const tokenKey = credix_token_response_data['token_key'];
                        self.doEnrolReq(tokenKey);
                    }
                });
            }
        }

        getCustomerCard() {
            let currentCustomerId = window.customerId;

            if (!currentCustomerId) {
                return Promise.resolve(false);
            }

            return fetch(`${window.SHOPIFY_SUB_APP}/3ds/card/${currentCustomerId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': Shopify.locale,
                    'X-Sig': window.sz_config.sub_app_key,
                    'X-Epoch': window.sz_config.timestamp
                }
            })
                .then((response) => response.json())
                .then((response) => {
                    return !!response.data;
                }).catch((err) => {
                    console.error(err);
                    return false;
                });
        }

        // On enrol failure, reload the page so the Credix card form re-initialises into
        // a fresh, working state. The Credix token is single-use: just re-showing the
        // hidden form leaves the widget without inputs and the Proceed button
        // unresponsive. The error toast is shown after the reload (see init()).
        handleEnrolFailure() {
            try { sessionStorage.setItem('credix_enrol_error', '1'); } catch (e) {}
            window.location.reload();
        }

        showEnrolError() {
            const msg = (typeof theme !== 'undefined' && theme.strings && theme.strings.credix && theme.strings.credix.msg_enrol_failed)
                || 'Payment could not be processed. Please check your card information and try again.';
            if (typeof showToast === 'function') {
                showToast(msg, 'error');
            }
        }

        doEnrolReq(tokenKey) {
            let loading_bg = document.querySelector(".loading_bg");
            const self = this;

            // Clear previous order data
            window.currentOrderNumber = null;
            window.currentOrderToken = null;
            this.orderNumber = null;
            this.orderToken = null;

            loading_bg.style.display = "block";

            const customerId = window.customerId;

            if (!customerId) {
                window.location.href = window.Shopify.routes.root + 'account';
                return;
            }

            // If this is an update card flow, use simplified logic
            if (this.isUpdateCard) {
                const content = {
                    tokenKey: tokenKey,
                    customerId: customerId,
                    changeCard: true
                };

                document.querySelector('#checkout-form').style.display = 'none';
                const div_waiter = document.querySelector("div[id='challenge_wait']");
                if (div_waiter) {
                    div_waiter.style.display = 'block';
                }


                fetch(`${window.SHOPIFY_SUB_APP}/3ds/enrol`, {
                    method: 'POST',
                    body: JSON.stringify(content),
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': Shopify.locale,
                        'X-Sig': window.sz_config.sub_app_key,
                        'X-Epoch': window.sz_config.timestamp
                    }
                })
                    .then((response) => response.json())
                    .then((r) => {
                        if (r) {
                            // Handle update card success
                            if (r?.change_card_success) {
                                // If source=ams, redirect to AMS URL
                                if (self.isAmsSource()) {
                                    window.location.href = `${window.url_suzu_wallet}/payment-method`;
                                } else {
                                    window.location.href = `${Shopify.routes.root}cart?changeCard=success`;
                                }
                                return;
                            }

                            // Handle update card error
                            if (r?.error) {
                                window.location.href = `${Shopify.routes.root}pages/purchase-fail`;
                                return;
                            }

                            let domparser = new DOMParser();
                            let doc = domparser.parseFromString(r.data, "application/xml");
                            const data = {};

                            if (typeof doc.getElementsByTagName("status")[0] !== 'undefined') {
                                data.status = doc.getElementsByTagName("status")[0].textContent;
                            } else {
                                data.status = 'maintenance';
                            }

                            // Handle 3DS challenge
                            if (!r?.data) {
                                throw new Error('3DS Enrol response is empty.');
                            }

                            if (data.status === 'success' || data.status === 'outside') {
                                if (typeof doc.getElementsByTagName("xid")[0] !== 'undefined' && typeof doc.getElementsByTagName("iframeUrl")[0] !== 'undefined') {
                                    data.xid = doc.getElementsByTagName("xid")[0].textContent;
                                    data.iframeUrl = doc.getElementsByTagName("iframeUrl")[0].textContent;
                                    if (typeof setPareqParams === 'function') {
                                        setPareqParams(
                                            data.xid,
                                            'PaReq',
                                            `${window.SHOPIFY_SUB_APP}/3ds/auth`,
                                            '2',
                                            data.iframeUrl
                                        );
                                    }
                                } else {
                                    console.log('3DS Enrol response missing required fields.');
                                }
                            }
                            setTimeout(() => {
                                loading_bg.style.display = "none";
                            }, 2500);
                        }
                    }).catch((err) => {
                        console.error(err);
                        self.handleEnrolFailure();
                    });
                return;
            }
            
            // Normal checkout flow (existing logic)
            // Check if resumeOrderId exists in URL
            const resumeOrderId = this.getUrlParameter('resumeOrderId');
            
            // Check if is_receiving_contract is true and get sale info (only if no resumeOrderId)
            const saleInfoPromise = !resumeOrderId && customerId
                ? this.getSaleInfo(customerId)
                : Promise.resolve({ success: false, exists: false });

            // Prepare promises based on whether resumeOrderId exists
            const promises = resumeOrderId 
                ? [Promise.resolve(null), this.getCustomerCard(), saleInfoPromise] // Skip cart-data if resumeOrderId exists
                : [getSection('cart-data'), this.getCustomerCard(), saleInfoPromise];

            Promise.all(promises)
                .then(([cartData, hasCard, saleInfoResult]) => {
                    let content;

                    if (resumeOrderId) {
                        // If resumeOrderId exists, use simplified content
                        content = {
                            tokenKey: tokenKey,
                            customerId: customerId,
                            spOrderId: resumeOrderId
                        };
                    } else {
                        // Normal flow: build content with items and saleContract
                        const response = JSON.parse(cartData);
                        if (!response.items) {
                            console.error('No items in cart');
                            return;
                        }

                        console.log(response.items);

                        content = {
                            tokenKey: tokenKey,
                            customerId: customerId,
                            items: response.items,
                            hasCard: false
                        };

                        // Add saleContract if is_receiving_contract is true
                        if (saleInfoResult.success && saleInfoResult.exists && saleInfoResult.data?.isReceivingContract) {
                            const saleContract = self.buildSaleContractFromSaleInfo(saleInfoResult);
                            if (saleContract) {
                                content.saleContract = saleContract;
                            }
                        }
                    }

                    document.querySelector('#checkout-form').style.display = 'none';
                    const div_waiter = document.querySelector("div[id='challenge_wait']");
                    if (div_waiter) {
                        div_waiter.style.display = 'block';
                    }

                    fetch(`${window.SHOPIFY_SUB_APP}/3ds/enrol`, {
                        method: 'POST',
                        body: JSON.stringify(content),
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept-Language': Shopify.locale,
                            'X-Sig': window.sz_config.sub_app_key,
                            'X-Epoch': window.sz_config.timestamp
                        }
                    })
                        .then((response) => response.json())
                        .then((r) => {
                            if (r) {
                                let domparser = new DOMParser();
                                let doc = domparser.parseFromString(r.data, "application/xml");
                                const data = {};

                                if (typeof doc.getElementsByTagName("status")[0] !== 'undefined') {
                                    data.status = doc.getElementsByTagName("status")[0].textContent;
                                } else {
                                    data.status = 'maintenance';
                                }

                                // If order was created successfully and no 3DS challenge needed, redirect immediately
                                // Status is NOT 'success' AND NOT 'outside' means no 3DS challenge required
                                if (r?.number && (data.status !== 'success' && data.status !== 'outside')) {
                                    // Clear cart before redirect
                                    self.clearCart();
                                    const urlPurchase = `${Shopify.routes.root}pages/purchase-success?orderNumber=${r.number}&token=${r.token || ''}`;
                                    window.location.href = urlPurchase;
                                    return;
                                }

                                // Store orderNumber and orderToken in window for global functions to access
                                if (r?.number) {
                                    window.currentOrderNumber = r.number;
                                    this.orderNumber = r.number;
                                }

                                if (r?.token) {
                                    window.currentOrderToken = r.token;
                                    this.orderToken = r.token;
                                }

                                if (!r?.data) {
                                    throw new Error('3DS Enrol response is empty.');
                                }

                                // If 3DS challenge is required
                                if (data.status === 'success' || data.status === 'outside') {
                                    if (typeof doc.getElementsByTagName("xid")[0] !== 'undefined' && typeof doc.getElementsByTagName("iframeUrl")[0] !== 'undefined') {
                                        data.xid = doc.getElementsByTagName("xid")[0].textContent;
                                        data.iframeUrl = doc.getElementsByTagName("iframeUrl")[0].textContent;
                                        if (typeof setPareqParams === 'function') {
                                            setPareqParams(
                                                data.xid,
                                                'PaReq',
                                                `${window.SHOPIFY_SUB_APP}/3ds/auth`,
                                                '2',
                                                data.iframeUrl
                                            );
                                        }
                                    } else {
                                        console.log('3DS Enrol response missing required fields.');
                                    }
                                }
                                setTimeout(() => {
                                    loading_bg.style.display = "none";
                                }, 2500);
                            }
                        }).catch((err) => {
                            console.error(err);
                            self.handleEnrolFailure();
                        });
                });
        }

        customizeCredixUI() {
            const self = this;
            this.customizeInterval = setInterval(function () {
                const cardNumberEl = document.querySelector('#credix_token_card_number');
                const cardCvvEl = document.querySelector('#credix_token_card_cvv');
                const cardExpMonthEl = document.querySelector('#credix_token_card_expires_month');
                const cardExpYearEl = document.querySelector('#credix_token_card_expires_year');
                const cardNameEl = document.querySelector('#credix_token_card_name');

                if (cardNumberEl && cardCvvEl && cardExpMonthEl && cardExpYearEl && cardNameEl) {
                    clearInterval(self.customizeInterval);
                    self.customizeInterval = null;

                    // 1) Thêm placeholder
                    cardNumberEl.placeholder = '1234 1234 1234 1234';
                    cardExpMonthEl.placeholder = 'MM';
                    cardExpYearEl.placeholder = 'YYYY';
                    cardCvvEl.placeholder = 'CVC';
                    cardNameEl.placeholder = theme.strings.credix.msg_placeholder_cardname;

                    // 2) Tạo row cho Expiry + CVC
                    // Tìm payment-input-wrapper chứa credix_token_card_number
                    let cardNumberWrapper = cardNumberEl.parentElement;
                    while (cardNumberWrapper && !cardNumberWrapper.classList.contains('payment-input-wrapper')) {
                        cardNumberWrapper = cardNumberWrapper.parentElement;
                    }
                    
                    // Nếu không tìm thấy payment-input-wrapper, dùng parent trực tiếp
                    if (!cardNumberWrapper) {
                        cardNumberWrapper = cardNumberEl.parentElement;
                    }
                    
                    // Tìm label "Card expiration date" 
                    let expiryLabel = document.querySelector('label[for="credix_token_card_expires_month"]');
                    if (!expiryLabel) {
                        const expiresParent = cardExpMonthEl.parentElement;
                        if (expiresParent) {
                            expiryLabel = expiresParent.querySelector('label');
                            if (!expiryLabel && expiresParent.parentElement) {
                                expiryLabel = expiresParent.parentElement.querySelector('label');
                            }
                        }
                    }

                    // Tìm label "Security code"
                    let cvvLabel = document.querySelector('label[for="credix_token_card_cvv"]');
                    if (!cvvLabel) {
                        const cvvParent = cardCvvEl.parentElement;
                        if (cvvParent) {
                            cvvLabel = cvvParent.querySelector('label');
                            if (!cvvLabel && cvvParent.parentElement) {
                                cvvLabel = cvvParent.parentElement.querySelector('label');
                            }
                        }
                    }

                    const row = document.createElement('div');
                    row.className = 'credix-row';

                    // Wrapper expiry
                    const expiryWrapper = document.createElement('div');
                    expiryWrapper.className = 'payment-input-wrapper';
                    
                    // Di chuyển label "Card expiration date" vào expiryWrapper (nếu tìm thấy)
                    if (expiryLabel) {
                        expiryWrapper.appendChild(expiryLabel);
                    }
                    
                    expiryWrapper.appendChild(cardExpMonthEl);
                    expiryWrapper.appendChild(cardExpYearEl);

                    // Wrapper CVC + icon lock
                    const cvcWrapper = document.createElement('div');
                    cvcWrapper.className = 'payment-input-wrapper payment-input-icon-right';
                    
                    // Di chuyển label "Security code" vào cvcWrapper (nếu tìm thấy)
                    if (cvvLabel) {
                        cvcWrapper.appendChild(cvvLabel);
                    }
                    
                    cvcWrapper.appendChild(cardCvvEl);

                    const lockIcon = document.createElement('img');
                    lockIcon.className = 'input-icon';
                    lockIcon.src = 'https://img.icons8.com/ios/24/lock--v1.png';
                    lockIcon.alt = 'Secure';
                    cvcWrapper.appendChild(lockIcon);

                    row.appendChild(expiryWrapper);
                    row.appendChild(cvcWrapper);

                    // Chèn row vào bên trong payment-input-wrapper, ngay sau credix_token_card_number
                    if (cardNumberWrapper) {
                        // Chèn row ngay sau input credix_token_card_number
                        if (cardNumberEl.nextSibling) {
                            cardNumberWrapper.insertBefore(row, cardNumberEl.nextSibling);
                        } else {
                            cardNumberWrapper.appendChild(row);
                        }
                    }

                    // 3) Bọc Name trong wrapper input cho đồng bộ
                    const nameParent = cardNameEl.parentElement;
                    const nameWrapper = document.createElement('div');
                    nameWrapper.className = 'payment-input-wrapper';
                    nameParent.parentElement.insertBefore(nameWrapper, nameParent);
                    nameWrapper.appendChild(nameParent);

                    // 4) Add event listeners to clear error messages when user starts typing
                    function clearErrorOnInput(field) {
                        if (field) {
                            const clearError = function() {
                                // For expiry date, error is after credix_token_card_expires_year (inside payment-input-wrapper)
                                if (field.id === 'credix_token_card_expires_month' || field.id === 'credix_token_card_expires_year') {
                                    const yearSelect = document.querySelector('#credix_token_card_expires_year');
                                    if (yearSelect) {
                                        // Check if error is right after yearSelect (inside wrapper)
                                        if (yearSelect.nextSibling && yearSelect.nextSibling.classList && yearSelect.nextSibling.classList.contains('field-error-message')) {
                                            yearSelect.nextSibling.remove();
                                        }
                                        // Also check inside wrapper (anywhere in wrapper)
                                        const wrapper = yearSelect.parentElement; // payment-input-wrapper
                                        if (wrapper) {
                                            const errorInside = wrapper.querySelector('.field-error-message');
                                            if (errorInside) {
                                                errorInside.remove();
                                            }
                                            // Also check after wrapper (in case error was inserted outside)
                                            if (wrapper.nextSibling && wrapper.nextSibling.classList && wrapper.nextSibling.classList.contains('field-error-message')) {
                                                wrapper.nextSibling.remove();
                                            }
                                        }
                                    }
                                } else {
                                    // For other fields, error is after the input itself
                                    if (field.nextSibling && field.nextSibling.classList && field.nextSibling.classList.contains('field-error-message')) {
                                        field.nextSibling.remove();
                                    }
                                    // For card number, also check after its direct wrapper (not the entire credix_new_card_area)
                                    if (field.id === 'credix_token_card_number') {
                                        const cardNumberWrapper = field.closest('.payment-input-wrapper');
                                        if (cardNumberWrapper && cardNumberWrapper.nextSibling && cardNumberWrapper.nextSibling.classList && cardNumberWrapper.nextSibling.classList.contains('field-error-message')) {
                                            cardNumberWrapper.nextSibling.remove();
                                        }
                                        // Check inside cardNumberWrapper only (not the entire credix_new_card_area)
                                        if (cardNumberWrapper) {
                                            const errorInside = cardNumberWrapper.querySelector('.field-error-message');
                                            if (errorInside) {
                                                errorInside.remove();
                                            }
                                        }
                                    } else {
                                        // For other fields (CVC, name), check after their wrapper
                                        const wrapper = field.closest('.payment-input-wrapper');
                                        if (wrapper && wrapper.nextSibling && wrapper.nextSibling.classList && wrapper.nextSibling.classList.contains('field-error-message')) {
                                            wrapper.nextSibling.remove();
                                        }
                                        // Check inside wrapper only
                                        if (wrapper) {
                                            const errorInside = wrapper.querySelector('.field-error-message');
                                            if (errorInside) {
                                                errorInside.remove();
                                            }
                                        }
                                    }
                                }
                            };
                            field.addEventListener('input', clearError);
                            field.addEventListener('change', clearError);
                        }
                    }

                    clearErrorOnInput(cardNumberEl);
                    clearErrorOnInput(cardExpMonthEl);
                    clearErrorOnInput(cardExpYearEl);
                    clearErrorOnInput(cardCvvEl);
                    clearErrorOnInput(cardNameEl);
                }
            }, 100);

            setTimeout(function () {
                if (self.customizeInterval) {
                    clearInterval(self.customizeInterval);
                    self.customizeInterval = null;
                }
            }, 5000);
        }

        clearCart() {
            fetch(window.Shopify.routes.root + 'cart/clear.js', {
                method: 'POST',
            })
                .then((response) => response.json())
                .then((response) => {
                    if(response.item_count === 0) {
                        document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
                    }
                })
                .catch((error) => {
                    console.error('Error clearing cart:', error);
                });
        }
    }

    customElements.define('creditcard-form', CreditcardForm);
}

// Global functions for Credix callbacks (called from credix_token_cvv2.js)
function _onPaResSuccess(data) {
    let loading_bg = document.querySelector(".loading_bg");
    console.log('PaResSuccess raw:', data);
    console.log('PaResSuccess type:', typeof data);
    
    // Parse data if it's a string
    let parsedData = data;
    if (typeof data === 'string') {
        try {
            parsedData = JSON.parse(data);
            console.log('PaResSuccess parsed:', parsedData);
        } catch (e) {
            console.error('Error parsing PaResSuccess data:', e);
            parsedData = data; // Fallback to original if parse fails
        }
    }
    
    // Check if this is an update card flow
    const updateCardBtn = document.querySelector('#btn_go_to_confirm_page[data-update-card="true"]');
    const isUpdateCard = !!updateCardBtn;
    
    // Helper function to get URL parameter
    function getUrlParameter(name) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(name);
    }
    
    if (isUpdateCard) {
        // Check if source=ams parameter exists
        const source = getUrlParameter('source');
        if (source === 'ams') {
            window.location.href = `${window.url_suzu_wallet}/payment-method`;
        } else {
            // Fallback: redirect to cart
            window.location.href = `${Shopify.routes.root}cart?changeCard=success`;
        }
    } else {
        // Checkout flow: redirect to purchase-success if order was created
        if (parsedData?.number) {
            // Clear cart before redirect
            clearCartForCredix();
            const urlPurchase = `${Shopify.routes.root}pages/purchase-success?orderNumber=${parsedData.number}&token=${parsedData.token || ''}`;
            window.location.href = urlPurchase;
            console.log('Redirecting to purchase-success:', urlPurchase);
        } else {
            // Fallback: redirect to cart
            window.location.href = `${Shopify.routes.root}cart?changeCard=success`;
        }
    }
    loading_bg.style.display = "none";
}

// Global function to clear cart (for use in global callbacks)
function clearCartForCredix() {
    fetch(window.Shopify.routes.root + 'cart/clear.js', {
        method: 'POST',
    })
        .then((response) => response.json())
        .then((response) => {
            if(response.item_count === 0) {
                document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
            }
        })
        .catch((error) => {
            console.error('Error clearing cart:', error);
        });
}

function _onError(error) {
    let loading_bg = document.querySelector(".loading_bg");
    console.log('Error raw:', error);
    console.log('Error type:', typeof error);
    
    // Parse error if it's a string
    let parsedError = error;
    if (typeof error === 'string') {
        try {
            parsedError = JSON.parse(error);
            console.log('Error parsed:', parsedError);
        } catch (e) {
            console.error('Error parsing error data:', e);
            parsedError = error; // Fallback to original if parse fails
        }
    }
    
    // Check if this is an update card flow
    const updateCardBtn = document.querySelector('#btn_go_to_confirm_page[data-update-card="true"]');
    const isUpdateCard = !!updateCardBtn;
    
    if (isUpdateCard) {
        window.location.href = `${Shopify.routes.root}pages/purchase-fail`;
    } else {
        // Check if orderNumber was stored in window (from doEnrolReq)
        const orderNumber = window.currentOrderNumber;
        const orderToken = window.currentOrderToken;
        
        if (orderNumber) {
            const urlPurchase = `${Shopify.routes.root}pages/purchase-fail?orderNumber=${orderNumber}&token=${orderToken || ''}`;
            window.location.href = urlPurchase;
            console.log('Redirecting to purchase-fail:', urlPurchase);
        } else {
            // For checkout flow, redirect to cart on error if no order number
            window.location.href = `${Shopify.routes.root}pages/purchase-fail`;
        }
    }
    console.log('Error details:', parsedError);

    loading_bg.style.display = "none";
}

function loadedChallenge() {
    const div_waiter = document.querySelector("div[id='challenge_wait']");
    if (div_waiter) {
        div_waiter.style.display = 'none';
    }
}

